import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { Store, select } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { BsModalService } from 'ngx-bootstrap/modal';
import * as numeral from 'numeral';
import { Observable } from 'rxjs';
import { filter, mergeMap, take } from 'rxjs/operators';

import { HistoryComponent } from '@shared/components/history/history.component';
import { ApproveStatusEnum } from '@shared/enum/approve-status.enum';
import { ModalButtonResponseEnum } from '@shared/enum/modal-button-response.enum';
import { RequestStatusEnum } from '@shared/enum/request-status.enum';
import { RequestPageModesEnum, RequestStepEnum, RequestTypeEnum } from '@shared/enum/request-step.enum';
import { TDStorePage } from '@shared/enum/td-store-page.enum';
import { AlertModalComponent } from '@shared/layouts';
import { ConfirmModalComponent } from '@shared/layouts/modals/confirm-modal/confirm-modal.component';
import { ConfirmWithMessageModalComponent } from '@shared/layouts/modals/confirm-with-message-modal/confirm-with-message-modal.component';
import { FullModalComponent } from '@shared/layouts/modals/full-modal/full-modal.component';
import {
  ErrorResponse,
  PortalModule,
  StoreRequestList,
  StoreRequestListSearchCriteria,
  TaskModuleUrl
} from '@shared/models';
import { AuditLog, HistoryType } from '@shared/models/audit-log.model';
import * as filterDropdown from '@shared/models/list-value/list-key-value.model';
import { AuthGuardService } from '@shared/services';
import { StoreRequestService } from '@shared/services/store-request.service';
import {
  StoreDeleteRequestAction,
  StoreDeleteResetAction,
  StoreRequestHistoryRequestAction,
  StoreRequestHistoryResponseAction,
  StoreRequestListRequestAction
} from '@shared/store/actions/store-request.actions';
import { StoreRequestState } from '@shared/store/reducers/store-request.reducers';
import {
  selectAllStoreRequestList,
  selectStoreDeleteStatus,
  selectStoreRequestHistory,
  selectStoreRequestList,
  selectStoreRequestListCriteria
} from '@shared/store/selectors/store-request.selectors';
import { AppStates } from '@shared/store/state/app.states';
import { CriteriaUtil } from '@shared/utils/criteria-util';
import { dateStringToTagCriteria, dateToStringCriteria, generateDateStringTag } from '@shared/utils/date-util';
import { merchantColorStatus } from '@shared/utils/merchant-color-status-util';
import { ModuleUtil } from '@shared/utils/module-util';
import { StoreWorkflowUtil } from '@shared/utils/store-workflow-util';

import { environment } from '../../../../environments/environment';
import { BaseSearchComponent } from '../../../base/base-search.component';
import { StoreRequestComponent } from '../store-request/store-request.component';

@Component({
  selector: 'app-store-request-list',
  templateUrl: './store-request-list.component.html',
  styleUrls: ['./store-request-list.component.scss']
})
export class StoreRequestListComponent
  extends BaseSearchComponent<StoreRequestListSearchCriteria, StoreRequestList, StoreRequestState>
  implements OnInit
{
  private localStore: Observable<any>;

  public storeRequestStatusFilter = filterDropdown.storeRequestStatusFilter;
  public storeRequestTypeFilter = filterDropdown.storeRequestTypeFilter;

  public dateFormat = environment.dateFormat;
  public minDate: Date;
  public maxDate: Date;
  public dateTag: string;
  public dateStringTag: string;
  public typeTag: string;
  public typeStringTag: string;
  public hasSearchCriteria: boolean;

  public page: TDStorePage;
  public type: RequestTypeEnum;

  public auditLogs$: Observable<AuditLog[]>;
  public numberFormat = '0,0';

  constructor(
    protected readonly store: Store<AppStates>,
    protected readonly modalService: BsModalService,
    protected fb: UntypedFormBuilder,
    protected authGuardService: AuthGuardService,
    protected storeRequestService: StoreRequestService,
    protected readonly translate: TranslateService,
    protected storeWorkflowUtil: StoreWorkflowUtil
  ) {
    super(store, modalService, selectAllStoreRequestList, selectStoreRequestList);
  }

  doInit() {
    this.page = TDStorePage.STORE_REQUEST;

    this.localStore = this.store.pipe(untilComponentDestroyed(this));
    this.localStore
      .pipe(select(selectStoreRequestListCriteria))
      .subscribe(criteriaObject => (this.currentPage = criteriaObject.page + 1));

    this.localStore.pipe(select(selectStoreDeleteStatus)).subscribe(isDeleteSuccess => {
      if (isDeleteSuccess) {
        this.alertSuccessModal('The request has been deleted.');
        this.store.dispatch(new StoreDeleteResetAction());
      }
    });
    this.auditLogs$ = this.localStore.pipe(select(selectStoreRequestHistory));
  }

  doDestroy() {
    // intentionally empty
  }

  createForm() {
    this.searchForm = this.fb.group({
      searchCriteria: [null],
      status: [this.storeRequestStatusFilter[0].value],
      startRequestDate: [null],
      endRequestDate: [null],
      type: [null]
    });
  }

  onchangeStatus(event: any) {
    this.currentPage = 1;
    this.criteriaObject = {
      ...this.criteriaObject,
      status: event.value,
      page: 0
    };
    this.doSearch(this.criteriaObject);
  }

  clearFilterDate() {
    this.setFirstPage();
    this.searchForm.controls['startRequestDate'].reset();
    this.searchForm.controls['endRequestDate'].reset();
    this.criteriaObject = {
      ...this.criteriaObject,
      page: 0,
      startRequestDate: null,
      endRequestDate: null
    };
    this.search(this.criteriaObject);
  }

  clearFilterType() {
    this.setFirstPage();
    this.searchForm.controls['type'].reset();
    this.criteriaObject = {
      ...this.criteriaObject,
      page: 0,
      type: null
    };
    this.search(this.criteriaObject);
  }

  clearAdvanceFilter() {
    this.searchForm.controls['startRequestDate'].reset();
    this.searchForm.controls['endRequestDate'].reset();
    this.searchForm.controls['type'].reset();

    this.setFirstPage();
    this.criteriaObject = {
      ...this.criteriaObject,
      startRequestDate: null,
      endRequestDate: null,
      type: null,
      page: 0
    };
    this.search(this.criteriaObject);
  }

  prepareSearchCriteriaTags() {
    this.dateTag = null;
    this.dateStringTag = null;
    this.typeTag = null;
    this.typeStringTag = null;

    const startRequestDate = dateStringToTagCriteria(this.criteriaObject.startRequestDate);
    const endRequestDate = dateStringToTagCriteria(this.criteriaObject.endRequestDate);
    const requestDate = generateDateStringTag({
      dateName: 'Requested Date',
      dateFrom: startRequestDate,
      dateTo: endRequestDate
    });

    this.dateStringTag = requestDate.dateStringTag;
    this.dateTag = requestDate.dateTag;

    if (this.criteriaObject.type && this.criteriaObject.type.length > 0) {
      this.typeStringTag = 'Request Type';

      const types = this.storeRequestTypeFilter
        .filter(data => {
          return this.criteriaObject.type.indexOf(data.value) > -1;
        })
        .map(status => status.label)
        .join(', ');

      this.typeTag = `"${types}"`;
    }
  }

  onAdvanceSubmit() {
    const formValue = this.searchForm.value;

    if (!formValue.startRequestDate && !formValue.endRequestDate && !formValue.type) {
      return;
    }

    let dateFrom = this.searchForm.value.startRequestDate;
    let dateTo = this.searchForm.value.endRequestDate;

    if (dateFrom && !isNaN(dateFrom.getTime())) {
      dateFrom = dateToStringCriteria(dateFrom);
    } else {
      dateFrom = null;
    }

    if (dateTo && !isNaN(dateTo.getTime())) {
      dateTo = dateToStringCriteria(dateTo, false);
    } else {
      dateTo = null;
    }

    this.isShowAdvanceSearch = false;
    this.setFirstPage();

    this.criteriaObject = {
      ...this.criteriaObject,
      startRequestDate: dateFrom,
      endRequestDate: dateTo,
      type: formValue.type && formValue.type.length > 0 ? formValue.type.toString() : null,
      page: 0
    };
    this.search(this.criteriaObject);
  }

  search(criteriaObj) {
    this.prepareSearchCriteriaTags();
    this.hasSearchCriteria = CriteriaUtil.hasCriteria(this.criteriaObject);
    this.store.dispatch(new StoreRequestListRequestAction(criteriaObj));
  }

  goToView(item) {
    const mode = RequestPageModesEnum.REQUEST_VIEW;
    const initialState = ModuleUtil.InitialState(
      item,
      StoreRequestComponent,
      mode,
      StoreRequestListComponent.getStoreRequestTitle(mode),
      TaskModuleUrl.STORE_REQUEST,
      PortalModule.STORE
    );

    this.modalService.show(FullModalComponent, {
      animated: false,
      backdrop: false,
      initialState
    });
  }

  goToEdit(item) {
    const mode = RequestPageModesEnum.REQUEST_EDIT;
    const initialState = ModuleUtil.InitialState(
      item,
      StoreRequestComponent,
      mode,
      StoreRequestListComponent.getStoreRequestTitle(mode),
      TaskModuleUrl.STORE_REQUEST,
      PortalModule.STORE
    );

    this.modalService.show(FullModalComponent, {
      animated: false,
      backdrop: false,
      initialState
    });
  }

  deleteStoreRequest(id) {
    const confirmModalRef = this.modalService.show(ConfirmModalComponent, {
      initialState: {
        title: 'Confirm',
        okText: 'Yes, delete',
        cancelText: 'Cancel',
        message: 'Are you sure you want to delete this request?'
      }
    });

    const modal = confirmModalRef.content.action
      .pipe(untilComponentDestroyed(this))
      .subscribe((result: ModalButtonResponseEnum) => {
        if (result === ModalButtonResponseEnum.OK) {
          this.store.dispatch(new StoreDeleteRequestAction({ storeId: id }));
        }

        if (modal) {
          modal.unsubscribe();
        }
      });
  }

  cancelStoreRequest(step: RequestStepEnum, requestNo: string) {
    const confirmModalRef = this.modalService.show(ConfirmWithMessageModalComponent, {
      initialState: {
        title: 'Confirm',
        message: `Are you sure you want to cancel request number <strong>&quot;${requestNo}&quot;</strong>?`,
        label: 'Reason',
        okText: 'Yes, cancel',
        cancelText: 'Cancel',
        isRequiredConfirmMessage: true
      }
    });

    const modal = confirmModalRef.content.action
      .pipe(
        untilComponentDestroyed(this),
        filter(result => result === ModalButtonResponseEnum.OK),
        mergeMap(() => {
          return this.storeRequestService
            .approveRequest({
              requestNo,
              status: ApproveStatusEnum.CANCELLED,
              step,
              comment: confirmModalRef.content.confirmMessage
            })
            .pipe(untilComponentDestroyed(this));
        })
      )
      .subscribe({
        next: () => {
          this.alertSuccessModal('The request has been cancelled.');
          this.doSearch(this.criteriaObject);
        },
        error: error => {
          this.alertErrorModal(error.error);
        },
        complete: () => {
          if (modal) {
            modal.unsubscribe();
          }
        }
      });
  }

  alertErrorModal(errorResponse: ErrorResponse & { limitQuotaStore?: number }) {
    const initialState = {
      title: 'Failed',
      message:
        errorResponse.code === '07021'
          ? this.translate.instant('ERROR_CODE.' + errorResponse.code + '.store', {
              limitQuotaStore: numeral(errorResponse.limitQuotaStore).format(this.numberFormat)
            })
          : this.translate.instant(errorResponse.translateKey, { context: errorResponse.message })
    };

    this.modalService.show(AlertModalComponent, {
      initialState
    });
  }

  doAfterVersionAlertModal() {
    this.doSearch(this.criteriaObject);
  }

  alertSuccessModal(message: string) {
    const initialState = {
      title: 'Success',
      message
    };

    const alertModal = this.modalService.show(AlertModalComponent, {
      initialState
    });

    const modal = alertModal.content.action.pipe(untilComponentDestroyed(this)).subscribe(() => {
      this.store.dispatch(new StoreRequestListRequestAction(this.criteriaObject));
      if (modal) {
        modal.unsubscribe();
      }
    });
  }

  showHistory(storeId: string, requestNo: string) {
    this.store.dispatch(new StoreRequestHistoryRequestAction({ storeId }));
    const initialState = {
      title: 'History',
      historyHeader: `Request No.: ${requestNo}`,
      action: HistoryType.REQUEST,
      historyType: HistoryType.STORE_REQUEST,
      auditLogs$: this.auditLogs$
    };

    this.modalService.show(HistoryComponent, {
      initialState
    });

    this.modalService.onHide.pipe(take(1)).subscribe(() => {
      this.store.dispatch(new StoreRequestHistoryResponseAction({ auditLogs: null }));
    });
  }

  getRequestNo(storeRequest: StoreRequestList) {
    return !storeRequest.requestNo || storeRequest.requestStatus === RequestStatusEnum.DRAFT
      ? '(None)'
      : storeRequest.requestNo;
  }

  getColorStatus(status: string): string {
    return merchantColorStatus(status);
  }

  setRouteTab() {
    // page-title + tabs now rendered by the parent app-store shell component
  }

  hasEditPermission(type: RequestTypeEnum, step: RequestStepEnum, status: RequestStatusEnum): boolean {
    return this.storeWorkflowUtil.hasEditPermission(type, this.page, step, status);
  }

  hasDeletePermission(type: RequestTypeEnum, step: RequestStepEnum, status: RequestStatusEnum): boolean {
    return this.storeWorkflowUtil.hasDeletePermission(type, this.page, step, status);
  }

  hasCancelPermission(status: RequestStatusEnum): boolean {
    return this.storeWorkflowUtil.hasCancelPermission(status);
  }

  hasViewHistoryPermission(status: RequestStatusEnum): boolean {
    return this.storeWorkflowUtil.hasViewHistoryPermission(status);
  }

  static getStoreRequestTitle(mode: RequestPageModesEnum) {
    let title: string;
    switch (mode) {
      case RequestPageModesEnum.REQUEST_CREATE:
        title = 'Create Store Request';
        break;
      case RequestPageModesEnum.REQUEST_VIEW:
        title = 'View Store Request';
        break;
      case RequestPageModesEnum.REQUEST_EDIT:
        title = 'Edit Store Request';
        break;
      default:
      // return null;
    }
    return title;
  }
}
